// ============================================================
// C3：渲染巡检按钮（截当前视角 → 服务端视觉检查 → 报告弹窗）
// AI 视觉不可用时服务端降级基础检查清单（功能始终可用）
// B4：报告一键转 AI 修复（预填指令并自动生成计划）
// ============================================================
import { useState } from 'react';
import { Button, List, Modal, Space, Spin, Tag, Typography, message } from 'antd';
import { ScanOutlined, ToolOutlined } from '@ant-design/icons';
import { sceneImageExportRegistry } from '../../stores/sceneImageExportStore';
import { postRenderInspect, type InspectReport } from '../../api/renderInspect';
import useSceneStore from '../../stores/sceneStore';
import { useInspectFixStore } from '../../stores/inspectFixStore';

const SEVERITY_META: Record<string, { color: string; label: string }> = {
  high: { color: 'red', label: '高' },
  medium: { color: 'orange', label: '中' },
  low: { color: 'blue', label: '低' },
};

export default function RenderInspectButton() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState<InspectReport | null>(null);
  const sceneName = useSceneStore((s) => s.currentScene?.name ?? '');

  const run = async () => {
    setOpen(true);
    setLoading(true);
    setReport(null);
    try {
      const canvas = sceneImageExportRegistry.getCanvas?.();
      if (!canvas) {
        message.warning('未找到 3D 画布（请等待场景加载完成后再试）');
        setOpen(false);
        return;
      }
      // 缩到 1280 宽再压缩，降低视觉模型耗时与请求体积
      const MAX_W = 1280;
      const scale = Math.min(1, MAX_W / Math.max(1, canvas.width));
      let dataUrl: string;
      if (scale < 1) {
        const off = document.createElement('canvas');
        off.width = Math.round(canvas.width * scale);
        off.height = Math.round(canvas.height * scale);
        off.getContext('2d')?.drawImage(canvas, 0, 0, off.width, off.height);
        dataUrl = off.toDataURL('image/jpeg', 0.72);
      } else {
        dataUrl = canvas.toDataURL('image/jpeg', 0.72);
      }
      const r = await postRenderInspect({ images: [{ name: '当前视角', dataUrl }], context: { sceneName } });
      setReport(r);
    } catch (e) {
      message.error(`渲染巡检失败：${(e as Error).message}`);
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button size="small" icon={<ScanOutlined />} onClick={() => void run()} data-testid="render-inspect-btn">
        渲染巡检
      </Button>
      <Modal title="渲染巡检报告" open={open} onCancel={() => setOpen(false)} footer={null} width={680} destroyOnHidden>
        <div data-testid="render-inspect-modal">
          {loading ? (
            <div style={{ textAlign: 'center', padding: '32px 0' }}>
              <Spin />
              <div style={{ marginTop: 12, color: '#888' }}>正在检查当前视角画面…</div>
            </div>
          ) : report ? (
            <>
              <Space align="start" style={{ marginBottom: 8 }} wrap>
                {report.aiUsed ? <Tag color="green">AI 视觉（{report.model}）</Tag> : <Tag>基础检查清单</Tag>}
                <Tag>{report.imageCount} 张截图</Tag>
                <Tag>{report.durationMs}ms</Tag>
              </Space>
              <Typography.Paragraph style={{ marginBottom: 12 }}>{report.summary}</Typography.Paragraph>
              {report.issues.length > 0 && (
                <>
                  <Typography.Title level={5} style={{ marginTop: 0 }}>
                    发现问题（{report.issues.length}）
                  </Typography.Title>
                  <List
                    size="small"
                    dataSource={report.issues}
                    renderItem={(it) => (
                      <List.Item>
                        <Space align="start">
                          <Tag color={SEVERITY_META[it.severity]?.color ?? 'default'}>{SEVERITY_META[it.severity]?.label ?? it.severity}</Tag>
                          <div>
                            <div style={{ fontWeight: 600 }}>{it.title}</div>
                            <div style={{ color: '#666', fontSize: 12 }}>{it.detail}</div>
                          </div>
                        </Space>
                      </List.Item>
                    )}
                  />
                </>
              )}
              <Typography.Title level={5}>优化建议</Typography.Title>
              <List
                size="small"
                dataSource={report.suggestions}
                renderItem={(s, i) => <List.Item>{i + 1}. {s}</List.Item>}
                locale={{ emptyText: '暂无建议' }}
              />
              <div style={{ marginTop: 16, textAlign: 'right' }}>
                <Button
                  type="primary"
                  icon={<ToolOutlined />}
                  data-testid="render-inspect-fix"
                  onClick={() => {
                    // B4：报告 → AI 修复指令（问题清单 + 建议，交给 AI 助手生成可执行 ops）
                    const issuesText = report.issues
                      .slice(0, 6)
                      .map((i) => `【${i.severity}】${i.title}：${i.detail}`)
                      .join('；');
                    const tips = report.suggestions.slice(0, 3).join('；');
                    const prompt = `请根据渲染巡检报告修复场景问题（尽量用可执行操作，如调整渲染预设/灯光/材质/显隐）：${issuesText || report.summary}${tips ? `。巡检建议：${tips}` : ''}`;
                    setOpen(false);
                    useInspectFixStore.getState().requestFix(prompt.slice(0, 480));
                    message.info('已生成修复指令，AI 助手正在制定修复计划…');
                  }}
                >
                  AI 修复建议
                </Button>
              </div>
            </>
          ) : null}
        </div>
      </Modal>
    </>
  );
}
